// Everything the export knows about ONE element, for a page that is not the
// model view.
//
// Owner, 2026-10-29: Find in Sheets & Model's 3D pane "is not using the full
// available component/fragments data from their json objects, and not
// showing full info on floating property panel like model view". The model
// view reads it from the exporter's record files through the record worker
// (helpers/recordWorker): the element's record (category, family, type,
// level, quantities, prices) and its parameter row from the per-category
// slice (BuiltIn / Family / Shared / Project / Equipment parameters). The 3D
// pane had only the search index's TYPE row, which is five fields about a
// group of elements.
//
// The same worker, the same documents, the same calls - only without the
// model view's tree. Registered under this page's own document ids, so a
// model view open in the same tab keeps its own.
import { url } from "api";
import { getProjectById } from "api/projects/getProjects";
import { getProjectModelDocuments } from "api/projects/modelSources";
import { getCompressedJsonFile } from "api/files/getJsonFile";
import { buildDocumentSets } from "helpers/documentSets";
import { buildPrefixOwners, documentForUid } from "helpers/documentPrefixOwners";
import { isStreamingPipelineEntry } from "helpers/streamingEntries";
import { registerDocument, chunkSpecFor, findRecord, findParams, isRecordTooLarge } from "helpers/recordWorker";
import { resolveToElement } from "webgl/js/loader/streaming/nestedParts";

// The parameter slice a record's row lives in: its top category with
// anything outside [A-Za-z0-9-] replaced - how the exporter names the file
// (the model view's rule, pages/ModelViewer).
export function sliceToken(record) {
  return String((record && (record.TopCategory || record.CategoryName)) || "Other").replace(/[^A-Za-z0-9-]/g, "_");
}

// Ask the document the uid names first; the others only when nobody owns
// its prefix (an export whose prefixes could not be learned).
export function askOrder(docs, ownerId) {
  if (!ownerId) return docs;
  return docs.filter((d) => d.docId === ownerId);
}

export function createElementRecords(projectId) {
  let docs = [];
  let owners = buildPrefixOwners([]);
  const cache = new Map();

  const ready = (async () => {
    const project = await getProjectById(projectId).catch(() => null);
    const rawMapping = (project && project.zipInfo && project.zipInfo.pathsmapping) || [];
    const rawPaths = (project && project.zipInfo && project.zipInfo.paths) || [];
    const mapping = [];
    const paths = [];
    for (let i = 0; i < rawMapping.length; i++) {
      if (isStreamingPipelineEntry(rawMapping[i])) continue;
      mapping.push(rawMapping[i]);
      paths.push(rawPaths[i]);
    }
    const sets = buildDocumentSets(mapping, paths);
    const registered = [];
    for (let i = 0; i < sets.length; i++) {
      const set = sets[i];
      if (!set.tree) continue;
      const docId = `split:${projectId}:${i}`;
      const slices = {};
      Object.keys(set.paramSlices || {}).forEach((t) => { slices[t] = `${url}/${set.paramSlices[t]}`; });
      let index = null;
      if (set.treeIndex) {
        // eslint-disable-next-line no-await-in-loop
        index = await getCompressedJsonFile(set.treeIndex, false).catch(() => null);
      }
      // eslint-disable-next-line no-await-in-loop
      await registerDocument(docId, `${url}/${set.tree}`, slices, chunkSpecFor(index, set, url));
      registered.push({ docId, tree: set.tree, name: set.name });
    }
    docs = registered;
    try {
      const answer = await getProjectModelDocuments(projectId);
      const byTree = new Map(((answer && answer.documents) || []).filter((d) => d.treePath).map((d) => [String(d.treePath), d]));
      owners = buildPrefixOwners(registered.map((d) => ({ docId: d.docId, prefixes: (byTree.get(String(d.tree)) || {}).prefixes || [] })));
    } catch (e) {
      owners = buildPrefixOwners([]);
    }
    return docs.length;
  })();

  // -> { uniqueId, record, params, document } | { uniqueId, error } | null
  async function lookup(rawUid) {
    if (!rawUid) return null;
    const uid = resolveToElement(String(rawUid));
    if (cache.has(uid)) return cache.get(uid);
    const job = (async () => {
      await ready;
      for (const d of askOrder(docs, documentForUid(owners, uid))) {
        let hit = null;
        try {
          // eslint-disable-next-line no-await-in-loop
          hit = await findRecord(d.docId, uid);
        } catch (e) {
          if (isRecordTooLarge(e)) return { uniqueId: uid, error: `${d.name}: the record file is too large to open in the browser` };
        }
        if (!hit || !hit.component) continue;
        // eslint-disable-next-line no-await-in-loop
        const params = await findParams(d.docId, hit.component.UniqueId || uid, sliceToken(hit.component)).catch(() => null);
        return { uniqueId: uid, record: hit.component, params: params || null, document: d.name };
      }
      return { uniqueId: uid, record: null, params: null, document: "" };
    })();
    cache.set(uid, job);
    // A failure is not remembered: the next hover asks again.
    job.then((r) => { if (!r || r.error) cache.delete(uid); }, () => cache.delete(uid));
    return job;
  }

  return { ready, lookup };
}
